import {
  generateSlug, rewriteLocale
} from 'services/API/helpers'
import {
  fetchData,
  getApiUrl
} from 'services/API'

const options = {
  // weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric',
}

const getMonth = (monthStr, locale) => {
  return new Date(monthStr + '-1-01').toLocaleDateString(locale, { month: 'long' })
}
const PAGE_SIZE = 3

const defaultQueryParams = {
  populate: 'deep',
  'sort[1]': 'date:desc',
}

// Fetch anni e mesi disponibili per una categoria dal BE (usato sulle pagine categoria)
const getNewsCategoryDateFilters = async (categorySlug, locale) => {
  const baseUrl = getApiUrl()
  const fullLocale = rewriteLocale(locale, 'full')
  const baseParams = `locale=${fullLocale}&populate=deep&sort[0]=date:desc`

  try {
    // Prima chiamata per ottenere i dati e il totale pagine
    const firstPage = await fetchData(
      `${baseUrl}/news-items?${baseParams}&pagination[page]=1&pagination[pageSize]=100`
    )
    let allData = firstPage?.data || []
    const pageCount = firstPage?.meta?.pagination?.pageCount || 1

    // Fetch pagine successive se necessario
    if (pageCount > 1) {
      const promises = []
      for (let i = 2; i <= pageCount; i++) {
        promises.push(fetchData(
          `${baseUrl}/news-items?${baseParams}&pagination[page]=${i}&pagination[pageSize]=100`
        ))
      }
      const pages = await Promise.all(promises)
      pages.forEach((page) => {
        if (page?.data) {
          allData = [...allData, ...page.data]
        }
      })
    }

    if (allData.length === 0) return null

    // Filtra per categoria in codice (il filtro relazionale causa errore 500 su Strapi)
    const filtered = allData.filter(({ attributes }) => {
      const catSlug = attributes.newsCategory?.data?.attributes?.slug
      return catSlug === categorySlug
    })

    if (filtered.length === 0) return null

    const years = [...new Set(filtered.map(({ attributes }) => attributes.year))].filter(Boolean)
    const months = [...new Set(filtered.map(({ attributes }) => attributes.month))].filter(Boolean)

    return {
      year: {
        label: 'Anno',
        items: years.map((y) => ({
          id: y,
          name: y,
        })),
      },
      month: {
        label: 'Mese',
        items: months.map((m) => ({
          id: m,
          name: m,
        })),
      },
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching category date filters:', error)
    return null
  }
}

// Fetch categorie dal BE
const getNewsCategories = async (fullLocale) => {
  const baseUrl = getApiUrl()
  const apiURL = `${baseUrl}/news-categories?locale=${fullLocale}&populate=deep`

  try {
    const response = await fetchData(apiURL)
    const { data } = response

    if (!data || data.length === 0) return []

    const categories = data.map(({
      id, attributes
    }) => {
      const parentSlug = attributes?.parent?.data?.attributes?.slug
      const slug = attributes?.slug
      return {
        id: slug || id,
        name: attributes?.title || 'Altro',
        slug,
        href: parentSlug ? `/${parentSlug}/${slug}` : `/${slug}`,
      }
    })

    return categories
  } catch (error) {
    console.error('Error fetching news categories:', error)
    return []
  }
}

export const normalizedContentData = (data) => {
  if (!data) return []

  return data.reduce((acc, curr) => {
    const {
      excerpt,
      year,
      month,
      locale: _itemLocale,
      date,
      title,
      preview,
      newsCategory,
      ...rest
    } = curr.attributes

    const itemLocale = rewriteLocale(_itemLocale, 'shortened')
    const itemsFilters = [
      {
        id: year,
        label: year,
        type: 'year',
      },
      {
        id: month,
        label: getMonth(month, itemLocale),
        type: 'month',
      },
    ]

    // Estrai titolo categoria per mostrarlo nella card
    const categoryTitle = newsCategory?.data?.attributes?.title || null

    // Aggiungi categoria se presente
    if (newsCategory?.data) {
      itemsFilters.push({
        id: newsCategory.data.id,
        label: newsCategory.data.attributes?.title || 'Senza categoria',
        type: 'newsCategory',
      })
    }

    const link = {
      href: generateSlug(curr.attributes).join('/'),
      target: '_self',
      locale: itemLocale,
    }

    const dateFormatted = new Date(date)
    const dateString = dateFormatted.toLocaleDateString(itemLocale, options)

    if (preview) {
      preview.title = title
    }

    const item = {
      ...rest,
      link,
      preview,
      locale: itemLocale,
      description: excerpt,
      filters: itemsFilters,
      date: dateString,
      categoryTitle,
    }

    acc.push(item)

    return acc
  }, [])
}

const NewsItemsArchive = async (data, locale, additionalData) => {
  const {
    showFilters,
    filters,
    pageSize,
  } = data

  const fullLocale = rewriteLocale(locale, 'full')

  const params = {
    locale: fullLocale,
    'pagination[page]': 1,
    'pagination[pageSize]': !!pageSize ? pageSize : PAGE_SIZE,
    ...defaultQueryParams,
  }

  // Se siamo su una pagina categoria, filtra automaticamente per quella categoria
  if (additionalData?.categorySlug) {
    params['filters[newsCategory][slug]'] = additionalData.categorySlug
  }

  let header = false

  const isCategoryPage = !!additionalData?.categorySlug

  // Mostra filtri sulla pagina principale (showFilters=true) e sempre sulle pagine categoria
  if (showFilters || isCategoryPage) {
    // Usa i filtri dal CMS; sulle pagine categoria, se non disponibili, li fetcha dinamicamente
    let filtersData = filters
    if (isCategoryPage && !filtersData) {
      filtersData = await getNewsCategoryDateFilters(additionalData.categorySlug, locale)
    }

    header = filtersData ?
      Object.keys(filtersData).map((key) => {
        const customLabel = data[`${key}FilterLabel`]
        const {
          label, items,
        } = filtersData[key]

        return {
          id: key,
          title: customLabel || label,
          items: items.map(({
            id, name, ...rest
          }) => {
            return {
              id,
              title: key === 'month' ? getMonth(name, locale) : name,
              ...rest,
            }
          }),
        }
      }) : []
  }

  // Build categoryTabs prop (separate from filters)
  let categoryTabs = null
  if (showFilters || isCategoryPage) {
    const categories = await getNewsCategories(fullLocale)
    const parentSlug = isCategoryPage
      ? additionalData.categoryParentSlug
      : null

    categoryTabs = {
      allLabel: data.categoryFilterLabel || 'All categories',
      items: categories.map(({
        id, name, slug, href,
      }) => ({
        id: slug || id,
        title: name || 'Altro',
        href,
      })),
      currentSlug: isCategoryPage ? additionalData.categorySlug : null,
      parentHref: parentSlug ? `/${parentSlug}` : null,
    }
  }

  const normalizedData = {
    ...data,
    params,
    categoryTabs,
    filters: {
      header,
      content: null,
    },
  }

  return normalizedData
}

export default NewsItemsArchive
